import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  AlertPreferenceUpdate,
  AppUser,
  Profile,
} from "../types";

type ProfileRow = {
  user_id: string;
  full_name: string;
  email: string;
  role: "admin" | "user";
  status: "active" | "inactive";
  client_id: string | null;
  can_view_all_clients: boolean;
  email_alerts_enabled: boolean;
  alert_14_days: boolean;
  alert_7_days: boolean;
  updated_at?: string;
};

function mapProfile(row: ProfileRow): Profile {
  return {
    userId: row.user_id,
    fullName: row.full_name,
    email: row.email,
    role: row.role === "admin" ? "Admin" : "User",
    status: row.status === "active" ? "Active" : "Inactive",
    clientId: row.client_id,
    canViewAllClients: row.can_view_all_clients,
    emailAlertsEnabled: row.email_alerts_enabled,
    alert14Days: row.alert_14_days,
    alert7Days: row.alert_7_days,
  };
}

const PROFILE_COLUMNS = [
  "user_id",
  "full_name",
  "email",
  "role",
  "status",
  "client_id",
  "can_view_all_clients",
  "email_alerts_enabled",
  "alert_14_days",
  "alert_7_days",
].join(",");

export async function fetchProfile(
  supabase: SupabaseClient,
  userId: string,
): Promise<Profile> {
  const { data, error } = await supabase
    .from("intrax_ss_profiles")
    .select(PROFILE_COLUMNS)
    .eq("user_id", userId)
    .single();

  if (error) throw error;
  const profile = mapProfile(data as unknown as ProfileRow);
  if (profile.status !== "Active") {
    throw new Error("Your INSCAPE profile is inactive.");
  }
  return profile;
}

export async function fetchUsers(
  supabase: SupabaseClient,
): Promise<AppUser[]> {
  const { data, error } = await supabase
    .from("intrax_ss_profiles")
    .select(`${PROFILE_COLUMNS},updated_at`)
    .order("full_name");

  if (error) throw error;
  return ((data ?? []) as unknown as ProfileRow[]).map((row) => ({
    ...mapProfile(row),
    updatedAt: row.updated_at ?? "",
  }));
}

export async function updatePreferences(
  supabase: SupabaseClient,
  userId: string,
  update: AlertPreferenceUpdate,
): Promise<void> {
  const { error } = await supabase
    .from("intrax_ss_profiles")
    .update({
      full_name: update.fullName.trim(),
      email_alerts_enabled: update.emailAlertsEnabled,
      alert_14_days: update.alert14Days,
      alert_7_days: update.alert7Days,
    })
    .eq("user_id", userId);
  if (error) throw error;
}
